// components/IPLocationSetup.js - Admin component for managing approved IP addresses
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    Grid,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Computer as ComputerIcon,
    LocationOn as LocationIcon,
    Add as AddIcon
} from '@mui/icons-material';
import {
    checkIPLocation,
    registerApprovedIP,
    getApprovedIPs,
    removeApprovedIP
} from '../services/IPVerification';

const IPLocationSetup = ({ orgId, userId }) => {
    const [locationName, setLocationName] = useState('');
    const [approvedIPs, setApprovedIPs] = useState([]);
    const [currentIPStatus, setCurrentIPStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: 'info' });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, ipId: null, ipName: '', ipAddress: '' });

    useEffect(() => {
        fetchApprovedIPs();
        checkCurrentIPStatus();
    }, [orgId]);

    const fetchApprovedIPs = async () => {
        setLoading(true);
        try {
            const ips = await getApprovedIPs(orgId);
            setApprovedIPs(ips);
        } catch (error) {
            console.error("Error fetching approved IPs:", error);
            setMessage({
                text: "Error loading approved IP addresses: " + error.message,
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const checkCurrentIPStatus = async () => {
        try {
            const status = await checkIPLocation(orgId);
            setCurrentIPStatus(status);
        } catch (error) {
            console.error("Error checking current IP:", error);
            setCurrentIPStatus({
                isApproved: false,
                currentIP: null,
                message: "Error checking current IP status"
            });
        }
    };

    const handleAddCurrentIP = async () => {
        if (!locationName.trim()) {
            setMessage({ text: "Please enter a name for this location", type: "warning" });
            return;
        }

        setLoading(true);
        try {
            const result = await registerApprovedIP(orgId, {
                name: locationName,
                userId: userId
            });

            setMessage({
                text: `Successfully registered current location "${locationName}" (${result.ipAddress})`,
                type: "success"
            });
            setLocationName('');

            // Refresh the lists
            await Promise.all([fetchApprovedIPs(), checkCurrentIPStatus()]);
        } catch (error) {
            console.error("Error registering IP:", error);
            setMessage({
                text: "Error registering IP address: " + error.message,
                type: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const openDeleteDialog = (ipId, ipName, ipAddress) => {
        setDeleteDialog({
            open: true,
            ipId,
            ipName,
            ipAddress
        });
    };

    const closeDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            ipId: null,
            ipName: '',
            ipAddress: ''
        });
    };

    const handleDeleteIP = async () => {
        if (!deleteDialog.ipId) return;

        setLoading(true);
        try {
            await removeApprovedIP(orgId, deleteDialog.ipId);

            setMessage({
                text: `IP address "${deleteDialog.ipAddress}" removed successfully`,
                type: "success"
            });

            // Refresh the data
            await Promise.all([fetchApprovedIPs(), checkCurrentIPStatus()]);
        } catch (error) {
            console.error("Error deleting IP:", error);
            setMessage({
                text: "Error removing IP address: " + error.message,
                type: "error"
            });
        } finally {
            setLoading(false);
            closeDeleteDialog();
        }
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                <LocationIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                IP-Based Location Verification
            </Typography>

            <Typography variant="body2" color="text.secondary" paragraph>
                This system verifies employee location by checking their internet IP address.
                All computers in your office will share the same public IP address.
            </Typography>

            {message.text && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ text: '', type: 'info' })}>
                    {message.text}
                </Alert>
            )}

            <Grid container spacing={3}>
                {/* Current IP Status */}
                <Grid item xs={12} md={6}>
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                                Current IP Status
                            </Typography>

                            {currentIPStatus ? (
                                <Box>
                                    <Typography variant="body2" gutterBottom>
                                        <strong>Your current IP:</strong> {currentIPStatus.currentIP || 'Unable to detect'}
                                    </Typography>

                                    <Alert
                                        severity={currentIPStatus.isApproved ? "success" : "warning"}
                                        sx={{ mt: 2 }}
                                    >
                                        {currentIPStatus.message}
                                    </Alert>

                                    {!currentIPStatus.isApproved && currentIPStatus.currentIP && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Add Current Location
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <TextField
                                                    size="small"
                                                    label="Location Name"
                                                    value={locationName}
                                                    onChange={(e) => setLocationName(e.target.value)}
                                                    placeholder="e.g., Main Office"
                                                    disabled={loading}
                                                    sx={{ flex: 1 }}
                                                />
                                                <Button
                                                    variant="contained"
                                                    onClick={handleAddCurrentIP}
                                                    disabled={loading || !locationName.trim()}
                                                    startIcon={<AddIcon />}
                                                >
                                                    Add
                                                </Button>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Approved IP Addresses List */}
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                        Approved Office Locations
                    </Typography>

                    {loading && approvedIPs.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : approvedIPs.length === 0 ? (
                        <Paper sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                No approved IP addresses configured yet.
                                Add your current office location to get started.
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper sx={{ border: '1px solid', borderColor: 'divider' }}>
                            <List disablePadding>
                                {approvedIPs.map((ip, index) => (
                                    <React.Fragment key={ip.id}>
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="subtitle2">
                                                            {ip.name}
                                                        </Typography>
                                                        {currentIPStatus?.currentIP === ip.ipAddress && (
                                                            <Chip
                                                                size="small"
                                                                label="Current"
                                                                color="success"
                                                                sx={{ height: 20 }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary">
                                                            IP: {ip.ipAddress}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Added: {ip.createdAt?.toLocaleString?.() || 'Unknown date'}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    color="error"
                                                    onClick={() => openDeleteDialog(ip.id, ip.name, ip.ipAddress)}
                                                    disabled={loading}
                                                    size="small"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                        {index < approvedIPs.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        </Paper>
                    )}
                </Grid>
            </Grid>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
                <DialogTitle>Remove IP Address</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove "{deleteDialog.ipName}" ({deleteDialog.ipAddress})?
                        <br /><br />
                        Employees will no longer be able to clock in from this IP address.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog}>Cancel</Button>
                    <Button onClick={handleDeleteIP} color="error" variant="contained">Remove</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default IPLocationSetup;